import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Eye, Upload, ArrowUp, ArrowDown, Info } from "@semo/icons";
import { toast } from "sonner";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@semo/shared-ui";
import type { CostEstimateData, CostItem } from "./types";
import { SAMPLE_COST_ESTIMATE } from "./sampleData";
import { computeCostEstimate, formatWon } from "./calc";
import { SummaryTable } from "./components/SummaryTable";
import { DetailPanel, type NewItemInput } from "./components/DetailPanel";
import { PreviewModal } from "./components/PreviewModal";

let itemIdCounter = 0;
const nextItemId = () => `item-${(itemIdCounter += 1)}`;

/** 목표(예산) 입력 — 상시 표시, 빈 값 허용 */
function TargetInput({
  value,
  onChange,
}: {
  value: number | null;
  onChange: (v: number | null) => void;
}) {
  const [focused, setFocused] = useState(false);
  const [text, setText] = useState(value === null ? "" : String(value));

  useEffect(() => {
    if (!focused) setText(value === null ? "" : String(value));
  }, [value, focused]);

  const display = focused ? text : value === null ? "" : formatWon(value);

  return (
    <input
      type="text"
      inputMode="numeric"
      value={display}
      placeholder="예산 입력"
      onFocus={() => {
        setFocused(true);
        setText(value === null ? "" : String(value));
      }}
      onBlur={() => setFocused(false)}
      onChange={(e) => {
        const raw = e.target.value.replace(/[^0-9]/g, "");
        setText(raw);
        onChange(raw === "" ? null : parseInt(raw, 10));
      }}
      className="w-28 rounded border border-slate-300 bg-white px-2 py-1 text-right text-sm font-semibold text-slate-800 outline-none placeholder:font-normal placeholder:text-slate-300 focus:border-primary"
      aria-label="예산(목표 공사비)"
    />
  );
}

/** 합계 - 목표(예산) 차이 표시 (초과=빨강▲ / 여유=초록▼) */
function TargetDiff({ diff }: { diff: number }) {
  if (diff === 0)
    return (
      <span className="text-xs font-semibold text-emerald-600">예산 일치</span>
    );
  const over = diff > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-xs font-semibold ${
        over ? "text-red-500" : "text-emerald-600"
      }`}
    >
      {over ? (
        <ArrowUp className="h-3.5 w-3.5" />
      ) : (
        <ArrowDown className="h-3.5 w-3.5" />
      )}
      {over ? "초과" : "여유"} {formatWon(Math.abs(diff))}원
    </span>
  );
}

type OpenCategory = "directMaterial" | "directLabor" | null;
type Category = "directMaterial" | "directLabor";

// 직접재료비 / 직접노무비 공용 접근 (순수 함수 — 컴포넌트 밖)
const getMajors = (d: CostEstimateData, cat: Category) =>
  cat === "directMaterial" ? d.directMaterial : d.directLabor;
const withMajors = (
  prev: CostEstimateData,
  cat: Category,
  majors: CostEstimateData["directMaterial"],
): CostEstimateData =>
  cat === "directMaterial"
    ? { ...prev, directMaterial: majors }
    : { ...prev, directLabor: majors };
const findItem = (
  d: CostEstimateData,
  cat: Category,
  majorCode: string,
  midCode: string,
  itemId: string,
) =>
  getMajors(d, cat)
    .find((m) => m.code === majorCode)
    ?.midWorks.find((w) => w.code === midCode)
    ?.items.find((it) => it.id === itemId);

/**
 * 원가계산서 메인 화면.
 * 요율(구성비 badge)을 수정하면 전체 금액/구성비가 실시간 재계산됩니다.
 */
export function CostEstimateView() {
  const [data, setData] = useState<CostEstimateData>(SAMPLE_COST_ESTIMATE);
  // 저장된 기준선 (이 값과 다르면 "수정됨")
  const [savedData, setSavedData] =
    useState<CostEstimateData>(SAMPLE_COST_ESTIMATE);
  const [openCategory, setOpenCategory] =
    useState<OpenCategory>("directMaterial");
  // 목표 공사비(예산). 상시 표시 — 실제로는 프로젝트 예산으로 미리 채워짐
  const [targetCost, setTargetCost] = useState<number | null>(null);
  // 미리보기 모달 + 공사명/업체명 (미리보기·엑셀 공용)
  const [previewOpen, setPreviewOpen] = useState(false);
  const [projectName, setProjectName] = useState("");
  const [vendorName, setVendorName] = useState("");

  // 패널 높이(px) — 드래그 핸들로 조절
  const containerRef = useRef<HTMLDivElement>(null);
  const [panelHeight, setPanelHeight] = useState(440);
  // 상단 "전체 물량 엑셀 업로드" 파일 입력
  const excelInputRef = useRef<HTMLInputElement>(null);

  const handleResize = useCallback((clientY: number) => {
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const next = rect.bottom - clientY; // 컨테이너 하단 기준 높이
    const min = 120;
    const max = rect.height - 48; // 상단 요약표 일부는 항상 보이게
    setPanelHeight(Math.max(min, Math.min(max, next)));
  }, []);

  // 특정 대공사>소공사의 품목 배열을 변환 (편집/추가/삭제 공용)
  const updateMid = (
    cat: Category,
    majorCode: string,
    midCode: string,
    fn: (items: CostItem[]) => CostItem[],
  ) => {
    setData((prev) =>
      withMajors(
        prev,
        cat,
        getMajors(prev, cat).map((maj) =>
          maj.code !== majorCode
            ? maj
            : {
                ...maj,
                midWorks: maj.midWorks.map((mid) =>
                  mid.code !== midCode ? mid : { ...mid, items: fn(mid.items) },
                ),
              },
        ),
      ),
    );
  };

  // 품목 편집 (품명/단위/수량/단가) → 금액·소계·총공사비 자동 재계산
  const editItem = (
    cat: Category,
    majorCode: string,
    midCode: string,
    itemId: string,
    patch: Partial<NewItemInput>,
  ) =>
    updateMid(cat, majorCode, midCode, (items) =>
      items.map((it) => (it.id !== itemId ? it : { ...it, ...patch })),
    );

  // 품목 추가 (직접 추가 / DB / 엑셀 공용)
  const addItems = (
    cat: Category,
    majorCode: string,
    midCode: string,
    newItems: NewItemInput[],
  ) =>
    updateMid(cat, majorCode, midCode, (items) => [
      ...items,
      ...newItems.map((n) => ({ id: nextItemId(), ...n })),
    ]);

  // 품목 삭제
  const deleteItem = (
    cat: Category,
    majorCode: string,
    midCode: string,
    itemId: string,
  ) =>
    updateMid(cat, majorCode, midCode, (items) =>
      items.filter((it) => it.id !== itemId),
    );

  // 엑셀 업로드 (파싱 연동 예정 — 현재는 파일 인식 안내)
  const handleUploadExcel = (file: File) => {
    toast.info(`"${file.name}" 인식됨 — 엑셀 물량 파싱은 곧 연동됩니다.`);
  };

  // ─── 수정됨(저장 전) 추적 ───
  const dirty = useMemo(
    () => ({
      indirectMaterial:
        data.indirectMaterialRate !== savedData.indirectMaterialRate,
      indirectLabor: data.indirectLaborRate !== savedData.indirectLaborRate,
      generalAdmin: data.generalAdminRate !== savedData.generalAdminRate,
      profit: data.profitRate !== savedData.profitRate,
      vat: data.vatRate !== savedData.vatRate,
      expense: (id: string) =>
        data.expenses.find((e) => e.id === id)?.rate !==
        savedData.expenses.find((e) => e.id === id)?.rate,
    }),
    [data, savedData],
  );

  const isItemDirty = (
    cat: Category,
    majorCode: string,
    midCode: string,
    itemId: string,
    field: "quantity" | "unitPrice",
  ) => {
    const cur = findItem(data, cat, majorCode, midCode, itemId);
    if (!cur) return false;
    const sav = findItem(savedData, cat, majorCode, midCode, itemId);
    if (!sav) return true; // 새로 추가된 품목(저장 전)
    return field === "quantity"
      ? cur.quantity !== sav.quantity
      : cur.unitPrice !== sav.unitPrice;
  };

  const dirtyCount = useMemo(() => {
    let n = 0;
    if (dirty.indirectMaterial) n++;
    if (dirty.indirectLabor) n++;
    if (dirty.generalAdmin) n++;
    if (dirty.profit) n++;
    if (dirty.vat) n++;
    data.expenses.forEach((e) => {
      if (dirty.expense(e.id)) n++;
    });
    const countItems = (cat: Category) =>
      getMajors(data, cat).forEach((maj) =>
        maj.midWorks.forEach((mid) =>
          mid.items.forEach((it) => {
            const sav = findItem(savedData, cat, maj.code, mid.code, it.id);
            if (!sav) {
              n++; // 새 품목
              return;
            }
            if (it.quantity !== sav.quantity) n++;
            if (it.unitPrice !== sav.unitPrice) n++;
          }),
        ),
      );
    countItems("directMaterial");
    countItems("directLabor");
    return n;
  }, [data, savedData, dirty]);

  const handleSave = () => setSavedData(data);
  const handleRevert = () => setData(savedData);

  const result = useMemo(() => computeCostEstimate(data), [data]);
  // 저장 기준선 계산값 (요약표/총공사비 변동량 표시용)
  const savedResult = useMemo(
    () => computeCostEstimate(savedData),
    [savedData],
  );

  return (
    <div ref={containerRef} className="relative min-h-full p-4 sm:p-6">
      {/* 상단 바 */}
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">
            공사원가계산서
          </h1>
          <p className="mt-1 text-sm leading-relaxed text-slate-500">
            진행하는 공사의 원가를 더 간편하게 측정해보세요.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {dirtyCount > 0 && (
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700">
                <span className="h-1.5 w-1.5 rounded-full bg-amber-400" />
                수정 {dirtyCount}개
              </span>
              <button
                type="button"
                onClick={handleSave}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary-hover"
              >
                저장
              </button>
              <button
                type="button"
                onClick={handleRevert}
                className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
              >
                되돌리기
              </button>
              <span className="h-4 w-px bg-slate-200" />
            </div>
          )}
          <div className="flex items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-1 text-slate-500">
              <span className="inline-flex items-center gap-0.5">
                예산
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-flex cursor-help text-slate-400">
                        <Info className="h-3.5 w-3.5" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="leading-relaxed">
                      <div className="whitespace-nowrap">
                        정해진 예산을 입력하면,
                      </div>
                      <div className="whitespace-nowrap">
                        합계와 비교해 여유분·초과분을 보여드려요.
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </span>
              <TargetInput value={targetCost} onChange={setTargetCost} />원
            </span>
            <span className="text-slate-300">·</span>
            <span className="text-slate-500">
              합계{" "}
              <b className="text-base font-semibold text-primary">
                {formatWon(result.totalCost)}원
              </b>
            </span>
            {targetCost !== null && (
              <>
                <span className="text-slate-300">·</span>
                <TargetDiff diff={result.totalCost - targetCost} />
              </>
            )}
          </div>
          <button
            type="button"
            onClick={() => setPreviewOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
          >
            <Eye className="h-4 w-4" /> 미리보기
          </button>
          <input
            ref={excelInputRef}
            type="file"
            accept=".xlsx,.xls,.csv"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleUploadExcel(file);
              e.target.value = "";
            }}
          />
          <button
            type="button"
            onClick={() => excelInputRef.current?.click()}
            className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-hover"
          >
            <Upload className="h-4 w-4" /> 전체 물량 엑셀 업로드
          </button>
        </div>
      </div>

      {/* 요약표 */}
      <SummaryTable
        data={data}
        result={result}
        savedResult={savedResult}
        openCategory={openCategory}
        onOpenCategory={setOpenCategory}
        onChangeIndirectMaterialRate={(rate) =>
          setData((prev) => ({ ...prev, indirectMaterialRate: rate }))
        }
        onChangeIndirectLaborRate={(rate) =>
          setData((prev) => ({ ...prev, indirectLaborRate: rate }))
        }
        onChangeExpenseRate={(id, rate) =>
          setData((prev) => ({
            ...prev,
            expenses: prev.expenses.map((e) =>
              e.id === id ? { ...e, rate } : e,
            ),
          }))
        }
        onChangeGeneralAdminRate={(rate) =>
          setData((prev) => ({ ...prev, generalAdminRate: rate }))
        }
        onChangeProfitRate={(rate) =>
          setData((prev) => ({ ...prev, profitRate: rate }))
        }
        onChangeVatRate={(rate) =>
          setData((prev) => ({ ...prev, vatRate: rate }))
        }
        dirty={dirty}
      />

      {/* 드릴다운 패널 */}
      {openCategory === "directMaterial" && (
        <DetailPanel
          title="직접재료비"
          subtotal={result.directMaterial}
          majors={data.directMaterial}
          dbKind="material"
          onClose={() => setOpenCategory(null)}
          height={panelHeight}
          onResize={handleResize}
          onEditItem={(maj, mid, item, patch) =>
            editItem("directMaterial", maj, mid, item, patch)
          }
          onAddItems={(maj, mid, items) =>
            addItems("directMaterial", maj, mid, items)
          }
          onDeleteItem={(maj, mid, item) =>
            deleteItem("directMaterial", maj, mid, item)
          }
          onUploadExcel={handleUploadExcel}
          isItemDirty={(maj, mid, item, field) =>
            isItemDirty("directMaterial", maj, mid, item, field)
          }
        />
      )}
      {openCategory === "directLabor" && (
        <DetailPanel
          title="직접노무비"
          subtotal={result.directLabor}
          majors={data.directLabor}
          dbKind="labor"
          onClose={() => setOpenCategory(null)}
          height={panelHeight}
          onResize={handleResize}
          onEditItem={(maj, mid, item, patch) =>
            editItem("directLabor", maj, mid, item, patch)
          }
          onAddItems={(maj, mid, items) =>
            addItems("directLabor", maj, mid, items)
          }
          onDeleteItem={(maj, mid, item) =>
            deleteItem("directLabor", maj, mid, item)
          }
          onUploadExcel={handleUploadExcel}
          isItemDirty={(maj, mid, item, field) =>
            isItemDirty("directLabor", maj, mid, item, field)
          }
        />
      )}

      <PreviewModal
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        data={data}
        result={result}
        projectName={projectName}
        vendorName={vendorName}
        onChangeProjectName={setProjectName}
        onChangeVendorName={setVendorName}
      />
    </div>
  );
}
